import { getNodeTemperatureColor, getNodeTemperatureStatus, } from "@ui/components/temperature";
import { SensorType, isSensorHidden } from "@ui/utils/sensors";
import { limitStatusOf } from "@ui/utils/sensor-limit-status";
import { RELAYED_SENSOR_TYPE_CODE } from "@ui/pages/nodes/edit-node/nodeTypeThreeForm";
/**
 * The Connected Nodes rules of a relayed sensor of a new access point (Edit Node: the internal and external temperature switches,
 * at least one on):
 * - both on: the internal temperature is the main one and the external one shows as a reading (`--` without a value);
 * - only the internal one: the main one, no external reading;
 * - only the external one: it becomes the main temperature (cyan tile with an info icon); the External Temperature Sensor Name
 *   replaces the node name, with the node id on the second line. Without a sensor name the row keeps the node name.
 */
export const toConnectedNodeDisplay = (node, t) => {
    var _a, _b, _c, _d, _e;
    const externalLabel = t("common:labels.externalTemperature");
    const sensorName = (_a = node.temperatureName) === null || _a === void 0 ? void 0 : _a.trim();
    const externalTooltip = sensorName
        ? `${externalLabel}\n${t("common:labels.name")}: ${sensorName}`
        : externalLabel;
    const nodeUid = (_b = node.nodeUid) !== null && _b !== void 0 ? _b : "";
    const nodeName = (_c = node.nodeName) !== null && _c !== void 0 ? _c : "";
    const externalAsMain = node.nodeTypeCode === RELAYED_SENSOR_TYPE_CODE &&
        isSensorHidden(node.disabledSensors, SensorType.InternalTemperature) &&
        !isSensorHidden(node.disabledSensors, SensorType.ExternalTemperature);
    if (!externalAsMain)
        return {
            nodeTemperature: (_d = node.nodeTemprature) !== null && _d !== void 0 ? _d : null,
            lowTemperature: node.lowTemperature,
            highTemperature: node.highTemperature,
            externalAsMain,
            title: nodeName,
            subtitle: nodeUid,
            externalTooltip,
        };
    return {
        nodeTemperature: (_e = node.externalTemperature) !== null && _e !== void 0 ? _e : null,
        lowTemperature: null,
        highTemperature: null,
        externalAsMain,
        title: sensorName || nodeName,
        subtitle: nodeUid,
        externalTooltip,
        tileInfoTooltip: externalLabel,
    };
};
const OUT_OF_RANGE_COLOR = "#FF5630";
/** The tile's status: the external temperature's own limit status when it is the main temperature. */
export const connectedNodeTileStatus = (row) => {
    var _a;
    const base = getNodeTemperatureStatus(row);
    if (!row.externalAsMain || base === "inactive")
        return base;
    return (_a = limitStatusOf(row.sensorStatuses, SensorType.ExternalTemperature)) !== null && _a !== void 0 ? _a : base;
};
/**
 * The tile's colour (see {@link connectedNodeTileStatus}); with the external temperature as the main one, a normal value gets
 * the cyan external tile (`TemperatureDisplay`'s `external`).
 */
export const connectedNodeTileColor = (row) => {
    if (!row.externalAsMain)
        return getNodeTemperatureColor(row);
    const status = connectedNodeTileStatus(row);
    return status === "high" || status === "low"
        ? OUT_OF_RANGE_COLOR
        : getNodeTemperatureColor(Object.assign(Object.assign({}, row), { lowTemperature: null, highTemperature: null }));
};
